import { useMemo } from 'react'
import moment from 'moment'
import { useIntl } from 'react-intl'

import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { PROVIDERS, DISNEY_CALIFORNIA_OFFER_IDS, DISNEY_ORLANDO_OFFER_ID } from 'constants/experience'
import { intlDateFormatter, useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { PrintDisneyCaliforniaVoucher } from 'components/Pages/ExperienceVoucher/PrintDisneyCaliforniaVoucher'
import { PrintDisneyOrlandoVoucher } from 'components/Pages/ExperienceVoucher/PrintDisneyOrlandoVoucher'
import { PrintLoungeVoucher } from 'components/Pages/ExperienceVoucher/PrintLoungeVoucher'
import { PrintCollinsonLoungeVoucher } from 'components/Pages/ExperienceVoucher/PrintCollinsonLoungeVoucher'
import { PrintStandardVoucher } from 'components/Pages/ExperienceVoucher/PrintStandardVoucher'

const INTL_SHORT_TIME_24_HOUR: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
}

type ExperienceVoucherLayoutType = 'disney' | 'collinson' | 'lounge' | 'standard'

interface Props {
  voucher: App.ExperienceVoucher
  bookingDetails?: App.ExperienceBookingDetails
  experienceOffer?: App.ExperienceOffer
  order?: App.Order
  isUniqueVoucherCode?: boolean
}

// Shared with ChatTripPlanner's in-chat experience voucher widget. If changing
// voucher layouts or rendered voucher details, coordinate with the Trip Planner
// team so customer-facing chat vouchers stay aligned with this page.
function isDisneyCaliforniaVoucher(bookingDetails?: App.ExperienceBookingDetails): boolean {
  return bookingDetails?.experienceOfferId
    ? DISNEY_CALIFORNIA_OFFER_IDS.includes(bookingDetails.experienceOfferId as any)
    : false
}

function isDisneyOrlandoVoucher(bookingDetails?: App.ExperienceBookingDetails): boolean {
  return bookingDetails?.experienceOfferId === DISNEY_ORLANDO_OFFER_ID
}

export function getExperienceVoucherLayoutType(
  voucher: App.ExperienceVoucher,
  bookingDetails?: App.ExperienceBookingDetails,
): ExperienceVoucherLayoutType {
  if (isDisneyCaliforniaVoucher(bookingDetails) || isDisneyOrlandoVoucher(bookingDetails)) {
    return 'disney'
  }

  if (bookingDetails?.provider === 'cln') {
    return 'collinson'
  }

  if ((voucher.offer?.vendorName || '').toLowerCase().includes(PROVIDERS.asp)) {
    return 'lounge'
  }

  return 'standard'
}

export default function ExperienceVoucherLayout(props: Props) {
  const { voucher, bookingDetails, experienceOffer, order, isUniqueVoucherCode } = props
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()

  const addressFormatted = useMemo((): string => {
    const location = voucher.location
    if (location) {
      return [location.address, location.city, location.state, location.postcode]
        .filter(Boolean)
        .join(', ')
    }
    return intl.formatMessage({
      id: 'fp2Z0Cyl8W1P2ZqxujDo',
      defaultMessage: 'N/A',
      description: 'Fallback text when experience voucher address is not available',
    })
  }, [voucher, intl])

  const notAvailableLabel = intl.formatMessage({
    id: 'okYhPvDXtMDv8nsLNqla',
    defaultMessage: 'N/A',
    description: 'Fallback text shown when an experience voucher field or booking detail is not available',
  })

  const expiryDateFormatted = useMemo<string>(() => {
    if (voucher.expiryDate) {
      return dateFormatter(
        voucher.expiryDate,
        INTL_DMY_CASUAL_FORMAT,
      )
    }
    return notAvailableLabel
  }, [dateFormatter, voucher, notAvailableLabel])

  const hasExpired = useMemo(() => {
    if (!voucher.expiryDate) return false
    return moment(voucher.expiryDate).isBefore(new Date())
  }, [voucher])

  const groupedExperienceItems = useMemo(() => {
    if (!order || !isUniqueVoucherCode || !order.experienceItems?.length) return undefined
    return Object.entries(
      order.experienceItems.reduce<Record<string, { name: string; count: number }>>((acc, item) => {
        const fareType = item.ticket?.fareType
        const name = item.ticket?.name
        if (fareType && name) {
          if (!acc[fareType]) {
            acc[fareType] = { name, count: 0 }
          }
          acc[fareType].count++
        }
        return acc
      }, {}),
    )
  }, [order, isUniqueVoucherCode])

  const isVoucherBased = useMemo(() => !experienceOffer?.hasCalendar, [experienceOffer])

  const date = useMemo(() => {
    if (isVoucherBased) return null
    return bookingDetails?.bookingStartDate ?
        intlDateFormatter(
          bookingDetails.bookingStartDate,
          INTL_DMY_CASUAL_FORMAT,
          intl.locale,
          'UTC',
        ) :
      notAvailableLabel
  }, [bookingDetails?.bookingStartDate, intl.locale, isVoucherBased, notAvailableLabel])

  const time = useMemo(() => {
    if (isVoucherBased) return null
    return bookingDetails?.bookingStartDate ?
        intlDateFormatter(
          bookingDetails.bookingStartDate,
          INTL_SHORT_TIME_24_HOUR,
          intl.locale,
          'UTC',
        ) :
      notAvailableLabel
  }, [bookingDetails?.bookingStartDate, intl.locale, isVoucherBased, notAvailableLabel])

  const layoutType = getExperienceVoucherLayoutType(voucher, bookingDetails)

  if (layoutType === 'disney') {
    return (
      <>
        {isDisneyCaliforniaVoucher(bookingDetails) && (
          <PrintDisneyCaliforniaVoucher
            address={addressFormatted}
            voucher={voucher}
            expiryDate={expiryDateFormatted}
          />
        )}
        {isDisneyOrlandoVoucher(bookingDetails) && (
          <PrintDisneyOrlandoVoucher
            address={addressFormatted}
            voucher={voucher}
            expiryDate={expiryDateFormatted}
          />
        )}
      </>
    )
  }

  if (layoutType === 'collinson') {
    return (
      <PrintCollinsonLoungeVoucher
        address={addressFormatted}
        voucher={voucher}
        experienceOffer={experienceOffer}
      />
    )
  }

  if (layoutType === 'lounge') {
    return (
      <PrintLoungeVoucher
        address={addressFormatted}
        voucher={voucher}
        experienceOffer={experienceOffer}
      />
    )
  }

  return (
    <PrintStandardVoucher
      voucher={voucher}
      experienceOffer={experienceOffer}
      address={addressFormatted}
      expiryDate={expiryDateFormatted}
      hasExpired={hasExpired}
      date={date}
      time={time}
      groupedExperienceItems={groupedExperienceItems}
      pickupPoint={bookingDetails?.pickupPoint}
    />
  )
}
